import { CustomerProfileModalResource } from '@appTypes/CustomerProfileModalResource';
import {
  IBpsScoreType,
  IDealsheetData,
  IDealsheetTab,
  ILegacyDealsheetData,
  MultiVehicleComparisonData,
  MultiVehicleComparisonOverrides
} from '@appTypes/DealsheetTypes';
import { InventoryResource } from '@appTypes/InvetoryResource';
import { CarfaxConnectUseCase } from '@enums/CarfaxConnectUseCase';
import { DealsheetActions, DealsheetActionType } from './DealsheetActions';

export type DealsheetState = {
  legacyDealsheetData: ILegacyDealsheetData | null;
  dealsheetData: Array<IDealsheetData> | null;
  activeTab: IDealsheetTab;
  selectedBpsScoreType: IBpsScoreType | null;
  isFetchingData: boolean;
  offerId: string | null;
  dealType: string | null;
  isMultiVehicleComparisonPageOpen: boolean;
  isCompareProductsPageOpen: boolean;
  isHistoryOpen: boolean;
  isMobileHamburgerNavOpen: boolean;
  isCustomerProfileOpen: boolean;
  isLoyaltyOpen: boolean;
  isNotesOpen: boolean;
  notes: Array<any>;
  isRecallModalOpen: boolean;
  isInventoryBrowserModalOpen: boolean;
  selctedInventoryVehicle: any;
  dealsheetPagination: Array<{
    name: string;
    bps: string;
    vin: string;
    dealerId: string;
    dealType: string;
  }>;
  temporaryReplacement: InventoryResource | null;
  customerProfileModalResource: CustomerProfileModalResource;
  carfaxSnapshotKey: string;
  carfaxSnapshotKeys: Array<{ name: CarfaxConnectUseCase; key: string }>;
  carfaxSnapshotKeyErrorMessages: Array<{ name: CarfaxConnectUseCase; message: string }>;
  tradeValue: number | null;
  paymentType: string | null;
  primaryAiMessages: any[];
  secondaryAiMessages: any[];
  replacementAiMessages: any[];
  AiIsLoading: boolean;
  isDealsheetReload: boolean;
  currentStoreName: string | null;
  selectedVehicleId: string | undefined;
  multiVehicleComparisonData: MultiVehicleComparisonData | null;
  multiVehicleComparisonOverrides: MultiVehicleComparisonOverrides | null;
};

export const initialUserState: DealsheetState = {
  legacyDealsheetData: null,
  dealsheetData: null,
  activeTab: IDealsheetTab.OFFER,
  isFetchingData: false,
  temporaryReplacement: null,
  offerId: null,
  dealType: null,
  selectedBpsScoreType: IBpsScoreType.BPS,
  isMultiVehicleComparisonPageOpen: false,
  isCompareProductsPageOpen: false,
  isHistoryOpen: false,
  isMobileHamburgerNavOpen: false,
  isCustomerProfileOpen: false,
  isLoyaltyOpen: false,
  isNotesOpen: false,
  notes: [],
  isRecallModalOpen: false,
  isInventoryBrowserModalOpen: false,
  selctedInventoryVehicle: null,
  dealsheetPagination: [
    {
      name: 'Scott Rautio',
      bps: '95',
      dealerId: 'GM0003',
      vin: '1GT49REY4LF201282',
      dealType: 'cash',
    },
    {
      name: 'Thomas Edwards',
      bps: '89',
      dealerId: 'GM0003',
      vin: '3GTU9AEF5LG428432',
      dealType: 'finance',
    },
    {
      name: 'Robert Blankenburg',
      bps: '93',
      dealerId: 'GM0003',
      vin: '3GKALPEV0ML392273',
      dealType: 'lease',
    },
  ],
  customerProfileModalResource: {},
  carfaxSnapshotKey: null,
  carfaxSnapshotKeys: [
    { name: CarfaxConnectUseCase.Current, key: null },
    { name: CarfaxConnectUseCase.ReplacementPrimary, key: null },
    { name: CarfaxConnectUseCase.ReplacementSecondary, key: null },
    { name: CarfaxConnectUseCase.TemporaryReplacement, key: null },
    { name: CarfaxConnectUseCase.TurnReplacement, key: null },
  ],
  carfaxSnapshotKeyErrorMessages: [
    { name: CarfaxConnectUseCase.Current, message: null },
    { name: CarfaxConnectUseCase.ReplacementPrimary, message: null },
    { name: CarfaxConnectUseCase.ReplacementSecondary, message: null },
    { name: CarfaxConnectUseCase.TemporaryReplacement, message: null },
    { name: CarfaxConnectUseCase.TurnReplacement, message: null },
  ],
  tradeValue: 0,
  paymentType: null,
  primaryAiMessages: [],
  secondaryAiMessages: [],
  replacementAiMessages: [],
  AiIsLoading: false,
  isDealsheetReload: false,
  currentStoreName: null,
  selectedVehicleId: undefined,
  multiVehicleComparisonData: null,
  multiVehicleComparisonOverrides: null
};

export const dealsheetReducer = (
  state: DealsheetState = initialUserState,
  action: DealsheetActions,
): DealsheetState => {
  switch (action.type) {
    case DealsheetActionType.SET_LEGACY_DEALSHEET_DATA:
      return {
        ...state,
        legacyDealsheetData: action.payload,
      };

    case DealsheetActionType.SET_DEALSHEET_DATA:
      return {
        ...state,
        dealsheetData: action.payload,
      };

    case DealsheetActionType.SET_DEALSHEET_PAGINATION:
      return {
        ...state,
        dealsheetPagination: action.payload,
      };

    case DealsheetActionType.SET_TEMPORARY_REPLACEMENT:
      return {
        ...state,
        temporaryReplacement: action.payload,
      };

    case DealsheetActionType.SET_IS_FETCHING_DATA:
      return {
        ...state,
        isFetchingData: action.payload,
      };

    case DealsheetActionType.SET_OFFER_ID:
      return {
        ...state,
        offerId: action.payload,
      };

    case DealsheetActionType.SET_DEAL_TYPE:
      return {
        ...state,
        dealType: action.payload,
      };

    case DealsheetActionType.SET_SELECTED_BPS_SCORE_TYPE:
      return {
        ...state,
        selectedBpsScoreType: action.payload,
      };

    case DealsheetActionType.SET_IS_MULTI_VEHICLE_COMPARISON_PAGE_OPEN:
      return {
        ...state,
        isMultiVehicleComparisonPageOpen: action.payload,
      };

    case DealsheetActionType.SET_IS_COMPARE_PRODUCTS_PAGE_OPEN:
      return {
        ...state,
        isCompareProductsPageOpen: action.payload,
      };

    case DealsheetActionType.SET_IS_HISTORY_OPEN:
      return {
        ...state,
        isHistoryOpen: action.payload,
      };

    case DealsheetActionType.SET_IS_MOBILE_HAMBURGER_NAV_OPEN:
      return {
        ...state,
        isMobileHamburgerNavOpen: action.payload,
      };

    case DealsheetActionType.SET_IS_CUSTOMER_PROFILE_OPEN:
      return {
        ...state,
        isCustomerProfileOpen: action.payload,
        isLoyaltyOpen: false,
        isNotesOpen: false,
      };

    case DealsheetActionType.SET_IS_LOYALTY_OPEN:
      return {
        ...state,
        isLoyaltyOpen: action.payload,
        isCustomerProfileOpen: false,
        isNotesOpen: false,
      };

    case DealsheetActionType.SET_IS_NOTES_OPEN:
      return {
        ...state,
        isNotesOpen: action.payload,
        isCustomerProfileOpen: false,
        isLoyaltyOpen: false,
      };

    case DealsheetActionType.SET_DEALSHEET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload,
        selectedBpsScoreType:
          action.payload === IDealsheetTab.OFFER || action.payload === IDealsheetTab.SHOWROOM
            ? null
            : state.selectedBpsScoreType,
      };

    case DealsheetActionType.SET_CUSTOMER_PROFILE_DATA:
      return {
        ...state,
        customerProfileModalResource: action.payload,
      };

    case DealsheetActionType.SET_IS_RECALL_MODAL_OPEN:
      return {
        ...state,
        isRecallModalOpen: action.payload,
      };

    case DealsheetActionType.SET_IS_INVENTORY_BROWSER_MODAL_OPEN:
      return {
        ...state,
        isInventoryBrowserModalOpen: action.payload,
      };

    case DealsheetActionType.SET_SELECTED_INVENTORY_VEHICLE:
      return {
        ...state,
        selctedInventoryVehicle: action.payload,
      };

    case DealsheetActionType.SET_CARFAX_SNAPSHOT_KEY:
      return {
        ...state,
        carfaxSnapshotKey: action.payload,
      };

    case DealsheetActionType.SET_CARFAX_SNAPSHOT_KEYS:
      return {
        ...state,
        carfaxSnapshotKeys: action.payload,
      };

    case DealsheetActionType.SET_CARFAX_SNAPSHOT_KEY_ERROR_MESSAGES:
      return {
        ...state,
        carfaxSnapshotKeyErrorMessages: action.payload,
      };

    case DealsheetActionType.SET_TRADE_VALUE:
      return {
        ...state,
        tradeValue: action.payload,
      };

    case DealsheetActionType.SET_PAYMENT_TYPE:
      return {
        ...state,
        paymentType: action.payload,
      };

    case DealsheetActionType.SET_PRIMARY_AI_MESSAGE:
      return {
        ...state,
        primaryAiMessages: action.payload,
      };

    case DealsheetActionType.SET_SECONDARY_AI_MESSAGE:
      return {
        ...state,
        secondaryAiMessages: action.payload,
      };

    case DealsheetActionType.SET_REPLACEMENT_AI_MESSAGE:
      return {
        ...state,
        replacementAiMessages: action.payload,
      };

    case DealsheetActionType.RESET_ALL_MESSAGES:
      return {
        ...state,
        primaryAiMessages: [],
        secondaryAiMessages: [],
        replacementAiMessages: [],
        AiIsLoading: false,
      };

    case DealsheetActionType.RESET_REPLACEMENT_AI_MESSAGE:
      return {
        ...state,
        replacementAiMessages: [],
      };

    case DealsheetActionType.RESET_PRIMARY_AI_MESSAGE:
      return {
        ...state,
        primaryAiMessages: [],
      };

    case DealsheetActionType.RESET_SECONDARY_AI_MESSAGE:
      return {
        ...state,
        secondaryAiMessages: [],
      };

    case DealsheetActionType.ADD_PRIMARY_AI_MESSAGE:
      return {
        ...state,
        primaryAiMessages: state.primaryAiMessages.some(
          (msg, index) =>
            msg.type === action.payload.type &&
            msg.isError &&
            index === state.primaryAiMessages.length - 1,
        )
          ? state.primaryAiMessages.map((msg, index) =>
            msg.type === action.payload.type &&
              msg.isError &&
              index === state.primaryAiMessages.length - 1
              ? action.payload
              : msg,
          )
          : [...state.primaryAiMessages, action.payload],
      };

    case DealsheetActionType.ADD_SECONDARY_AI_MESSAGE:
      return {
        ...state,
        secondaryAiMessages: state.secondaryAiMessages.some(
          (msg, index) =>
            msg.type === action.payload.type &&
            msg.isError &&
            index === state.secondaryAiMessages.length - 1,
        )
          ? state.secondaryAiMessages.map((msg, index) =>
            msg.type === action.payload.type &&
              msg.isError &&
              index === state.secondaryAiMessages.length - 1
              ? action.payload
              : msg,
          )
          : [...state.secondaryAiMessages, action.payload],
      };

    case DealsheetActionType.ADD_REPLACEMENT_AI_MESSAGE:
      return {
        ...state,
        replacementAiMessages: state.replacementAiMessages.some(
          (msg, index) =>
            msg.type === action.payload.type &&
            msg.isError &&
            index === state.replacementAiMessages.length - 1,
        )
          ? state.replacementAiMessages.map((msg, index) =>
            msg.type === action.payload.type &&
              msg.isError &&
              index === state.replacementAiMessages.length - 1
              ? action.payload
              : msg,
          )
          : [...state.replacementAiMessages, action.payload],
      };

    case DealsheetActionType.SET_IS_LOADING:
      return {
        ...state,
        AiIsLoading: action.payload,
      };

    case DealsheetActionType.SET_IS_DEALSHEET_RELOAD:
      return {
        ...state,
        isDealsheetReload: action.payload,
      };

    case DealsheetActionType.SET_SELECTED_VEHICLE_ID:
      return {
        ...state,
        selectedVehicleId: action.payload,
      };

    case DealsheetActionType.SET_MULTI_VEHICLE_COMPARISON_DATA:
      return {
        ...state,
        multiVehicleComparisonData: action.payload
      };

    case DealsheetActionType.SET_MULTI_VEHICLE_COMPARISON_OVERRIDES:
      return {
        ...state,
        multiVehicleComparisonOverrides: action.payload
      };

    default:
      return state;
  }
};
